import React, { memo, useCallback, useEffect, useState } from 'react';
import { Grid } from '@mui/material';
import { useCharts } from './useCharts';
import { useBenchmarkLevel } from './useBenchmarkLevel';
import { useLoadingStore, useOperatorsStore } from '../store/loadingStore';

type FacilityData = {
  type: string;
  total: number;
  count: number;
  pop: number;
};

type EquipmentData = {
  type: string;
  total: number;
  count: number;
  pop: number;
};

type Dataset = {
  dimensions: string[];
  source: Record<string, any>[];
};

function transformDataToDataset(data: FacilityData[]): Dataset {
  const dimensions = ['facility type', 'Population EF', 'Total Emission'];

  const source = data.map((facility) => ({
    'facility type': facility.type,
    'Total Emission': facility.total,
    'Population EF': facility.pop,
  }));

  return {
    dimensions,
    source,
  };
}

function transformEquipDataToDataset(data: EquipmentData[]): Dataset {
  const dimensions = ['equipment type', 'Population EF', 'Total Emission'];

  const source = data.map((facility) => ({
    'equipment type': facility.type,
    'Total Emission': facility.total,
    'Population EF': facility.pop,
  }));

  return {
    dimensions,
    source,
  };
}

export const BenchmarkLevel: React.FC<{
  period: string;
  time: string;
}> = memo(({ period, time }) => {
  const { leakerEquipResults, leakerFacilityResults } = useBenchmarkLevel({
    period,
    time,
  });

  const { ChartCollection8 } = useCharts();
  const [activeTab] = useState(0);
  const [facilityChartdata, setFacilityChartdata] = useState<
    Dataset | undefined
  >(undefined);
  const [equipChartdata, setEquipChartdata] = useState<Dataset | undefined>(
    undefined,
  );
  const setLoading = useLoadingStore((state) => state.setLoading);

  const selectedOperator = useOperatorsStore((state) => state.selectedOperator);

  useEffect(() => {
    const isDataReady = () => {
      return Object.entries(leakerFacilityResults).length > 0;
    };

    if (isDataReady()) {
      setLoading(false, 100);
    }
  }, [activeTab, leakerFacilityResults, leakerEquipResults, setLoading]);

  useEffect(() => {
    if (selectedOperator && Object.entries(leakerFacilityResults).length > 0) {
      const transformedFacilityData = transformDataToDataset(
        leakerFacilityResults[selectedOperator || 1],
      );

      setFacilityChartdata(transformedFacilityData);

      const transformedEquipmentData = transformEquipDataToDataset(
        leakerEquipResults[selectedOperator || 1],
      );

      setEquipChartdata(transformedEquipmentData);
    }
  }, [selectedOperator, leakerFacilityResults]);

  const renderActiveTabContent = useCallback(() => {
    switch (activeTab) {
      case 0:
        return (
          <>
            <Grid
              container
              direction="row"
              sx={{
                justifyContent: 'flex-start',
                alignItems: 'flex-start',
              }}
              width={'100%'}
              padding={1}
            >
              <Grid
                item
                width={'48%'}
                paddingRight={'2%'}
                className={'allcharts withDivider doubleSize'}
              >
                {facilityChartdata && (
                  <ChartCollection8
                    allResults={equipChartdata}
                    tabType={'facility'}
                    compact={true}
                    title={'Average equipment emission rate (kg/h)'}
                  />
                )}
              </Grid>
              <Grid
                item
                width={'48%'}
                paddingLeft={'2%'}
                className={'allcharts doubleSize'}
              >
                {facilityChartdata && (
                  <ChartCollection8
                    allResults={facilityChartdata}
                    tabType={'facility'}
                    compact={true}
                    title={'Average facility emission rate (kg/h)'}
                  />
                )}
              </Grid>
            </Grid>
          </>
        );
      default:
        return null;
    }
  }, [activeTab, leakerEquipResults, leakerFacilityResults, facilityChartdata]);

  return (
    <div className="tabs" style={{ marginTop: '125px', padding: '0 10px' }}>
      <div className="tab-content">{renderActiveTabContent()}</div>
    </div>
  );
});
